import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, AlertCircle, Activity, Shield, MapPin, X, IndianRupee } from 'lucide-react';

const heatColor = (value, max) => {
  if (!max || value === 0) return '#f1f5f9';
  const intensity = Math.min(value / max, 1);
  const r = Math.round(219 - (219 - 30) * intensity);
  const g = Math.round(234 - (234 - 64) * intensity);
  const b = Math.round(254 - (254 - 175) * intensity);
  return `rgb(${r}, ${g}, ${b})`;
};

const fmtAmount = (n) => {
  if (n == null) return '—';
  const v = Number(n);
  if (Number.isNaN(v) || v === 0) return '—';
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  return `₹${v.toLocaleString('en-IN')}`;
};

const ProductivityRail = ({ data, loading, onHeatmapClick, onClose, amounts }) => {
  if (loading && !data) {
    return (
      <aside className="w-full xl:w-[320px] shrink-0 space-y-3" data-testid="productivity-rail-loading">
        {[1, 2, 3, 4].map(i => (
          <Card key={i} className="animate-pulse"><CardContent className="p-4 h-24" /></Card>
        ))}
      </aside>
    );
  }
  if (!data) return null;

  const { heatmap = [], aging_buckets = {}, decision_mix = {}, override_count, override_rate,
          top_backlog_district } = data;

  const maxHeat = heatmap.reduce((m, r) => Math.max(m, r.Existing || 0, r.New || 0), 0);
  const overrideRatePct = override_rate != null ? (override_rate * 100).toFixed(1) : '0.0';
  const overrideAlert = override_rate > 0.15;

  return (
    <aside className="w-full xl:w-[320px] shrink-0 space-y-3" data-testid="productivity-rail">
      <div className="flex items-center justify-between px-1">
        <div className="text-xs uppercase tracking-wide font-semibold text-gray-600">Productivity</div>
        {onClose && (
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 xl:hidden" onClick={onClose} data-testid="productivity-rail-close">
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {/* Heatmap — District × [Existing, New] (chairman: merged Renewal + Enhanced) */}
      <Card data-testid="widget-heatmap">
        <CardContent className="p-3.5">
          <div className="flex items-center gap-1.5 mb-2">
            <Activity className="h-3.5 w-3.5 text-blue-700" />
            <div className="text-[11px] uppercase tracking-wide font-semibold text-gray-600">Pending Backlog</div>
          </div>
          <div className="text-[10px] text-gray-400 mb-2">District × Relationship (click to filter)</div>
          {heatmap.length === 0 ? (
            <div className="text-[11px] text-gray-400 py-4 text-center">No pending cases</div>
          ) : (
            <div className="overflow-x-auto -mx-1">
              <table className="w-full text-[10px]">
                <thead>
                  <tr>
                    <th className="text-left font-semibold text-gray-500 px-1.5 py-1">District</th>
                    <th className="font-semibold text-gray-500 px-1 py-1">Existing</th>
                    <th className="font-semibold text-gray-500 px-1 py-1">New</th>
                  </tr>
                </thead>
                <tbody>
                  {heatmap.map(row => (
                    <tr key={row.district}>
                      <td className="text-gray-700 font-medium px-1.5 py-1">{row.district}</td>
                      {['Existing', 'New'].map(bucket => (
                        <td
                          key={bucket}
                          className="text-center px-1 py-1 cursor-pointer font-semibold tabular-nums"
                          style={{
                            background: heatColor(row[bucket] || 0, maxHeat),
                            color: (row[bucket] || 0) > (maxHeat * 0.55) ? 'white' : '#0f172a',
                          }}
                          onClick={() => onHeatmapClick && onHeatmapClick({ district: row.district, relationship: bucket })}
                          data-testid={`heatmap-cell-${row.district}-${bucket}`}
                        >
                          {row[bucket] || 0}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Amounts Card — 30-day disposed cases (chairman request: cases · applied · sanctioned · New vs Existing) */}
      <Card data-testid="widget-amounts-card">
        <CardContent className="p-3.5 space-y-2">
          <div className="flex items-center gap-1.5">
            <IndianRupee className="h-3.5 w-3.5 text-emerald-700" />
            <div className="text-[11px] uppercase tracking-wide font-semibold text-gray-600">Amounts (30D)</div>
          </div>
          {(() => {
            const a = amounts || {};
            const newA = a.new || { cases: 0, requested: 0, sanctioned: 0 };
            const exA = a.existing || { cases: 0, requested: 0, sanctioned: 0 };
            return (
              <div className="space-y-2 text-[11px]">
                <div className="grid grid-cols-3 font-semibold text-gray-500 uppercase tracking-wide text-[9px] pb-1 border-b border-gray-100">
                  <div>&nbsp;</div>
                  <div className="text-right">New</div>
                  <div className="text-right">Existing</div>
                </div>
                <div className="grid grid-cols-3 items-center" data-testid="amounts-row-cases">
                  <span className="text-gray-600">No. of Cases</span>
                  <span className="text-right font-semibold tabular-nums text-gray-900">{newA.cases || 0}</span>
                  <span className="text-right font-semibold tabular-nums text-gray-900">{exA.cases || 0}</span>
                </div>
                <div className="grid grid-cols-3 items-center" data-testid="amounts-row-requested">
                  <span className="text-gray-600">Applied/Requested</span>
                  <span className="text-right font-semibold tabular-nums text-gray-900">{fmtAmount(newA.requested)}</span>
                  <span className="text-right font-semibold tabular-nums text-gray-900">{fmtAmount(exA.requested)}</span>
                </div>
                <div className="grid grid-cols-3 items-center" data-testid="amounts-row-sanctioned">
                  <span className="text-gray-600">Sanctioned</span>
                  <span className="text-right font-semibold tabular-nums text-gray-900">{fmtAmount(newA.sanctioned)}</span>
                  <span className="text-right font-semibold tabular-nums text-gray-900">{fmtAmount(exA.sanctioned)}</span>
                </div>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Age-wise Pendency — 3 / 7 / 15 day buckets */}
      <Card data-testid="widget-aging-buckets">
        <CardContent className="p-3.5">
          <div className="flex items-center gap-1.5 mb-2">
            <AlertCircle className="h-3.5 w-3.5 text-amber-600" />
            <div className="text-[11px] uppercase tracking-wide font-semibold text-gray-600">Age-wise Pendency</div>
          </div>
          <div className="space-y-1.5">
            {[
              { key: 'over_3d', label: '3 days', color: '#fef9c3', fg: '#854d0e' },
              { key: 'over_7d', label: '7 days', color: '#fef3c7', fg: '#92400e' },
              { key: 'over_15d', label: '15 days', color: '#fee2e2', fg: '#991b1b' },
            ].map(b => (
              <div key={b.key} className="flex items-center justify-between" data-testid={`aging-${b.key}`}>
                <span className="text-[11px] text-gray-600">{b.label}</span>
                <Badge className="text-[10px] tabular-nums border-none" style={{ background: b.color, color: b.fg }}>
                  {aging_buckets[b.key] ?? 0}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Disposal Status (30D) */}
      <Card data-testid="widget-decision-mix">
        <CardContent className="p-3.5">
          <div className="flex items-center gap-1.5 mb-2">
            <TrendingUp className="h-3.5 w-3.5 text-blue-700" />
            <div className="text-[11px] uppercase tracking-wide font-semibold text-gray-600">Disposal Status (30D)</div>
          </div>
          <div className="flex gap-2">
            <div className="flex-1 text-center py-2 rounded" style={{ background: '#dcfce7' }}>
              <div className="text-xl font-bold tabular-nums" style={{ color: '#166534' }}>{decision_mix.APPROVED || 0}</div>
              <div className="text-[9px] uppercase tracking-wide text-green-800">Approved</div>
            </div>
            <div className="flex-1 text-center py-2 rounded" style={{ background: '#fee2e2' }}>
              <div className="text-xl font-bold tabular-nums" style={{ color: '#991b1b' }}>{decision_mix.REJECTED || 0}</div>
              <div className="text-[9px] uppercase tracking-wide text-red-800">Rejected</div>
            </div>
            <div className="flex-1 text-center py-2 rounded" style={{ background: '#e0e7ff' }}>
              <div className="text-xl font-bold tabular-nums" style={{ color: '#3730a3' }}>{decision_mix.RETURNED || 0}</div>
              <div className="text-[9px] uppercase tracking-wide text-indigo-800">Returned</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Override rate */}
      <Card data-testid="widget-override-rate">
        <CardContent className="p-3.5">
          <div className="flex items-center gap-1.5 mb-2">
            <Shield className="h-3.5 w-3.5 text-amber-700" />
            <div className="text-[11px] uppercase tracking-wide font-semibold text-gray-600">Override Rate (30D)</div>
          </div>
          <div className="flex items-end gap-2">
            <div className={`text-xl font-bold tabular-nums ${overrideAlert ? 'text-red-600' : 'text-slate-800'}`}>
              {overrideRatePct}%
            </div>
            <div className="text-[11px] text-gray-500 pb-0.5">({override_count || 0} cases)</div>
          </div>
          {overrideAlert && <div className="text-[10px] text-red-600 mt-1">High override rate — review governance</div>}
        </CardContent>
      </Card>

      {/* Highest Backlog (only insight kept — chairman removed Slowest Journey) */}
      <Card data-testid="widget-top-backlog-district">
        <CardContent className="p-3.5">
          <div className="flex items-center gap-1.5 mb-1">
            <MapPin className="h-3.5 w-3.5 text-red-600" />
            <div className="text-[10px] uppercase tracking-wide font-semibold text-gray-500">Highest Backlog</div>
          </div>
          <div className="text-xs text-gray-800">
            {top_backlog_district
              ? <><span className="font-semibold">{top_backlog_district.district}</span> — {top_backlog_district.count} pending</>
              : 'No backlog'}
          </div>
        </CardContent>
      </Card>
    </aside>
  );
};

export default ProductivityRail;
